import React, { useState, useEffect } from "react";
import HeaderContainer from "./Header";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getApiUrl } from "../config";
import { env } from "../helpers/GlobalStateProvider";
import { setToken } from "../helpers/tokenManager";
import { Card, Flex, Heading, Input, Stack, Text } from "@chakra-ui/react";
import { Button } from "../components/ui/button";
import { Field } from "../components/ui/field";

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const email = searchParams.get("email");

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log("URL is:", window.location.href);
      console.log("Token from URL:", token);
    }
  }, [token]);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!password || !confirmPassword) {
      setError("Please fill out both fields.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (!token) {
      setError("Reset token is missing.");
      return;
    }
    setError("");
    setLoading(true);

    const baseUrl = getApiUrl(env);
    const fullUrl = `${baseUrl}/v3/apikey/reset_password?token=${token}&email=${encodeURIComponent((email as any) ?? '')}`;
    if (process.env.NODE_ENV === 'development') console.log("Reset password URL:", fullUrl, token);

    try {
      const response = await axios.post(fullUrl, {
        token,
        newPassword: password,
      });

      const newToken = response.data?.newToken;
      if (newToken) {
        setToken(newToken);
      }

      if (response.status === 200) {
        navigate("/reset_password_success");
      }
    } catch (err: any) {
      setError(err.response?.data?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Flex
      minHeight="100vh"
      width="100%"
      maxWidth="1392px"
      margin="0 auto"
      paddingTop="24px"
      paddingBottom="24px"
      direction="column"
      gap="24px"
    >
      {/* Header */}
      <HeaderContainer behavior="signin" />

      {/* Main container to center the content */}
      <Flex flex={1} justifyContent="center" alignItems="center" padding="20px">
        {/* Content wrapper for max width */}
        <Stack maxWidth="480px" width="100%" alignItems="center" gap={8}>
          {/* Page title */}
          <Heading
            as="h1"
            textAlign="center"
            color="#18181b"
            fontSize="24px"
            fontWeight="bold"
            letterSpacing="-0.4px"
            lineHeight="32px"
          >
            {token ? "Reset your password" : "Invalid or missing link"}
          </Heading>

          <Card.Root width="100%">
            <Card.Body>
              {token ? (
                <Stack gap={6}>
                  <Stack gap={4}>
                    <Field label="Enter your new password">
                      <Input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        fontSize="sm"
                      />
                    </Field>
                    <Field label="Confirm your new password">
                      <Input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        fontSize="sm"
                      />
                    </Field>
                  </Stack>

                  {error && (
                    <Text fontSize="sm" color="#dc2626">
                      {error}
                    </Text>
                  )}

                  <Button
                    onClick={handleSubmit}
                    loading={loading}
                    variant="solid"
                    colorPalette="purple"
                    bg="#9333ea"
                    width="100%"
                  >
                    Reset password
                  </Button>
                </Stack>
              ) : (
                <Stack gap={4} alignItems="center" textAlign="center">
                  <Text fontSize="md" color="#52525b" lineHeight="24px">
                    Please use the link sent to your email to reset your
                    password.
                  </Text>
                  <Button
                    onClick={() => navigate("/signin")}
                    variant="solid"
                    colorPalette="purple"
                    bg="#9333ea"
                  >
                    Back to sign in
                  </Button>
                </Stack>
              )}
            </Card.Body>
          </Card.Root>
        </Stack>
      </Flex>
    </Flex>
  );
};

export default ResetPasswordPage;
